import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Tabs,
  Badge,
  Alert,
  Modal,
  Input,
  InputNumber,
  Tooltip,
  Typography,
  Collapse,
  Spin,
  message,
  Descriptions,
  Progress,
  Select,
  Popconfirm,
  Divider,
  Checkbox,
} from "antd";
import {
  ClusterOutlined,
  ThunderboltOutlined,
  FileTextOutlined,
  ShoppingCartOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DeleteOutlined,
  SwapOutlined,
  MinusCircleOutlined,
  ArrowRightOutlined,
  PlayCircleOutlined,
  SaveOutlined,
  ReloadOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  EditOutlined,
} from "@ant-design/icons";
import JobDetail from "../job/JobDetail";
import {
  applyBatchSuggestion,
  calculateJobSpecs,
  getJobById,
  revertBatchSuggestion,
  rejectBatchSuggestion,
  applyBoxReductionSuggestion,
  rejectBoxReductionSuggestion,
  updateJob,
  autoSelectMachines,
} from "../../api/jobApi";
import {
  getBatchPlanSuggestions,
  createPlannedBatch,
  getPlannedBatches,
  getBatchMRP,
  generateBatchInstructions,
  saveBatchInstructions,
  getSavedInstructions,
  addJobsToBatch,
  removeJobFromBatch,
  moveJobBetweenBatches,
  deletePlannedBatch,
  getBoxSizeSuggestion,
  getInstructionLogs,
} from "../../api/batchPlanningApi";

const { Text, Title } = Typography;
const { Panel } = Collapse;
const { TextArea } = Input;

const priorityColors = {
  urgent: "red",
  high: "orange",
  normal: "blue",
  low: "default",
};

function BatchPlanning() {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState({ batches: [], suggestions: [] });
  const [plannedBatches, setPlannedBatches] = useState([]);
  const [rsSortOrder, setRsSortOrder] = useState("asc"); // "asc" | "desc"
  const [jobModalId, setJobModalId] = useState(null);
  const [instructionLogs, setInstructionLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState({});
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [batchNotes, setBatchNotes] = useState("");
  const [activeBatchRS, setActiveBatchRS] = useState(null);
  const [mrpData, setMrpData] = useState(null);
  const [mrpLoading, setMrpLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("suggestions");
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [moveJob, setMoveJob] = useState(null); // { jobId, fromBatchId, jobNumber }
  const [targetBatchId, setTargetBatchId] = useState(null);
  const [addToBatchModalOpen, setAddToBatchModalOpen] = useState(false);
  const [addToBatchTarget, setAddToBatchTarget] = useState(null);
  const [instructionsData, setInstructionsData] = useState(null);
  const [instructionsLoading, setInstructionsLoading] = useState(false);
  const [addToExistingModal, setAddToExistingModal] = useState(false);
  const [addToExistingJobs, setAddToExistingJobs] = useState([]); // [{id, job_number, roll_size, ...}]
  const [addToExistingTargetBatch, setAddToExistingTargetBatch] = useState(null);
  const [selectedReductionOptions, setSelectedReductionOptions] = useState({});
  const [manualMoveModalOpen, setManualMoveModalOpen] = useState(false);
  const [manualMoveContext, setManualMoveContext] = useState(null);
  const [manualTargetMode, setManualTargetMode] = useState("suggested");
  const [manualTargetRs, setManualTargetRs] = useState(null);
  const [manualTargetBatchId, setManualTargetBatchId] = useState(null);
  const [manualDims, setManualDims] = useState({ length: null, width: null, height: null });
  const [manualCheckLoading, setManualCheckLoading] = useState(false);
  const [manualApplyLoading, setManualApplyLoading] = useState(false);
  const [manualCheckResult, setManualCheckResult] = useState(null);
  const [manualTargetHint, setManualTargetHint] = useState(null);
  const [manualReductionSuggestion, setManualReductionSuggestion] = useState(null);
  const [manualSuggestionLoading, setManualSuggestionLoading] = useState(false);
  const [manualCurrentSpecs, setManualCurrentSpecs] = useState({ roll_size: null, cutting_size: null });
  const [manualOverrideEnabled, setManualOverrideEnabled] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sugData, batchData] = await Promise.all([
        getBatchPlanSuggestions(),
        getPlannedBatches(),
      ]);
      setSuggestions(sugData);
      setPlannedBatches(batchData);
    } catch (err) {
      message.error("Failed to load batch planning data");
    }
    setLoading(false);
  }, []);

  const handleApplySuggestion = async (row) => {
    try {
      await applyBatchSuggestion(row.job_id, row.suggested_batch_rs, row.new_boxes_per_sheet);
      message.success(`RS changed to ${row.suggested_batch_rs}" — refresh to see updated grouping`);
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to apply suggestion");
    }
  };

  const handleRevertSuggestion = async (row) => {
    try {
      await revertBatchSuggestion(row.job_id);
      message.success("RS restored to original");
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to revert suggestion");
    }
  };

  const handleRejectSuggestion = async (row) => {
    try {
      await rejectBatchSuggestion(row.job_id, row.suggested_batch_rs);
      message.success("Suggestion rejected");
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to reject suggestion");
    }
  };

  const handleApplyBoxReduction = async (row) => {
    const optionIndex = selectedReductionOptions[row.job_id] ?? 0;
    const option = row.options?.[optionIndex];
    if (!option) {
      message.warning("Select a valid reduction option");
      return;
    }
    try {
      await applyBoxReductionSuggestion(row.job_id, option);
      message.success("Box size reduction applied");
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to apply box size reduction");
    }
  };

  const handleRejectBoxReduction = async (row) => {
    try {
      await rejectBoxReductionSuggestion(row.job_id);
      message.success("Box size reduction suggestion rejected");
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to reject box size reduction");
    }
  };

  const sootheToActual = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return 0;
    const whole = Math.trunc(parsed);
    const dec = Math.round(Math.abs(parsed - whole) * 10);
    if (dec >= 0 && dec <= 8) return whole + (parsed < 0 ? -dec : dec) / 8;
    return parsed;
  };

  const resetManualMoveState = () => {
    setManualMoveModalOpen(false);
    setManualMoveContext(null);
    setManualTargetMode("suggested");
    setManualTargetRs(null);
    setManualTargetBatchId(null);
    setManualDims({ length: null, width: null, height: null });
    setManualCheckResult(null);
    setManualTargetHint(null);
    setManualReductionSuggestion(null);
    setManualSuggestionLoading(false);
    setManualCurrentSpecs({ roll_size: null, cutting_size: null });
    setManualOverrideEnabled(false);
  };

  const getManualCurrentRollSize = () => (
    manualCurrentSpecs.roll_size
    ?? manualMoveContext?.job?.roll_size
    ?? manualMoveContext?.job?.rs
    ?? manualMoveContext?.job?.order_item?.roll_size
    ?? manualMoveContext?.job?.order_item?.rs
    ?? null
  );

  const getManualCurrentCuttingSize = () => (
    manualCurrentSpecs.cutting_size
    ?? manualMoveContext?.job?.cutting_size
    ?? manualMoveContext?.job?.cs
    ?? manualMoveContext?.job?.order_item?.cutting_size
    ?? manualMoveContext?.job?.order_item?.cs
    ?? null
  );

  const loadManualReductionSuggestion = async (jobId, targetValue, isPlanned = false) => {
    if (!jobId || !targetValue) {
      setManualReductionSuggestion(null);
      return;
    }

    const currentActual = sootheToActual(getManualCurrentRollSize());
    const targetRollSize = isPlanned
      ? plannedBatches.find((b) => b.id === targetValue)?.roll_size
      : targetValue;
    const targetActual = sootheToActual(targetRollSize);

    if (!(currentActual > 0) || !(targetActual > 0) || targetActual >= currentActual) {
      setManualReductionSuggestion(null);
      return;
    }

    try {
      setManualSuggestionLoading(true);
      const suggestion = await getBoxSizeSuggestion(jobId);
      if (suggestion && sootheToActual(suggestion.target_rs) === targetActual) {
        setManualReductionSuggestion(suggestion);
      } else {
        setManualReductionSuggestion(null);
      }
    } catch (_) {
      setManualReductionSuggestion(null);
    } finally {
      setManualSuggestionLoading(false);
    }
  };

  const openManualMoveModal = async (jobId, fromBatchId = null, initialTargetBatchId = null) => {
    try {
      setManualApplyLoading(false);
      setManualCheckLoading(true);
      const job = await getJobById(jobId);
      let derivedRollSize = job.roll_size ?? job.rs ?? job.order_item?.roll_size ?? job.order_item?.rs ?? null;
      let derivedCuttingSize = job.cutting_size ?? job.cs ?? job.order_item?.cutting_size ?? job.order_item?.cs ?? null;

      if ((!derivedRollSize || !derivedCuttingSize) && job.length && job.width) {
        try {
          const derived = await calculateJobSpecs({
            length: job.length,
            width: job.width,
            height: job.height || 0,
            unit_type: job.unit_type,
            subcategory: job.subcategory,
            casting_type: job.casting_type,
            quantity: job.production_quantity,
            selected_machines: job.selected_machines || [],
            layers: job.layers || [],
            ply: job.ply,
          });
          derivedRollSize = derived?.optimized?.roll_size ?? derived.RS ?? derived.roll_size ?? derivedRollSize;
          derivedCuttingSize = derived?.optimized?.cutting_size ?? derived.CS ?? derived.cutting_size ?? derivedCuttingSize;
        } catch (_) {
          // Keep the saved values when derivation fails.
        }
      }

      setManualMoveContext({ job, fromBatchId });
      setManualCurrentSpecs({ roll_size: derivedRollSize, cutting_size: derivedCuttingSize });
      setManualDims({
        length: job.length ?? null,
        width: job.width ?? null,
        height: job.height ?? null,
      });
      setManualTargetMode(initialTargetBatchId ? "planned" : "suggested");
      setManualTargetBatchId(initialTargetBatchId || null);
      setManualTargetRs(initialTargetBatchId ? null : null);
      setManualCheckResult(null);
      setManualTargetHint(null);
      setManualReductionSuggestion(null);
      setManualOverrideEnabled(false);
      setManualMoveModalOpen(true);
    } catch (err) {
      message.error("Failed to load job details");
    } finally {
      setManualCheckLoading(false);
    }
  };

  const getManualTargetActualRs = () => {
    if (manualTargetMode === "planned") {
      const batch = plannedBatches.find((b) => b.id === manualTargetBatchId);
      return batch ? sootheToActual(batch.roll_size) : 0;
    }
    return sootheToActual(manualTargetRs);
  };

  const handleCheckManualMove = async () => {
    if (!manualMoveContext?.job) return;
    const targetRsActual = getManualTargetActualRs();
    if (!(targetRsActual > 0)) {
      message.warning("Select a target RS or planned batch first");
      return;
    }

    const payload = {
      length: manualDims.length,
      width: manualDims.width,
      height: manualDims.height || 0,
      unit_type: manualMoveContext.job.unit_type,
      subcategory: manualMoveContext.job.subcategory,
      casting_type: manualMoveContext.job.casting_type,
      quantity: manualMoveContext.job.production_quantity,
      MAX_RS: targetRsActual + 0.5,
      MAX_CS: manualMoveContext.job.cutting_size ? sootheToActual(manualMoveContext.job.cutting_size) + 0.5 : 999,
      selected_machines: manualMoveContext.job.selected_machines || [],
      layers: manualMoveContext.job.layers || [],
      ply: manualMoveContext.job.ply,
    };

    try {
      setManualCheckLoading(true);
      const result = await calculateJobSpecs(payload);
      const computedRollSize = result?.optimized?.roll_size ?? result.RS ?? result.roll_size;
      const computedCuttingSize = result?.optimized?.cutting_size ?? result.CS ?? result.cutting_size;
      const computedBoxesPerSheet = result?.optimized?.boxes_per_sheet ?? result.boxes_per_sheet ?? result.M ?? 1;
      const computedSheetsPerBox = result?.optimized?.sheets_per_box ?? result.sheets_per_box ?? result.N ?? 1;
      const resultRsActual = sootheToActual(computedRollSize);
      const exactSuggestedMatch = Math.abs(resultRsActual - targetRsActual) < 0.001;
      const feasible = resultRsActual > 0 && (
        manualTargetMode === "suggested"
          ? exactSuggestedMatch
          : resultRsActual <= targetRsActual
      );
      setManualCheckResult({
        ...result,
        computedRollSize,
        computedCuttingSize,
        computedBoxesPerSheet,
        computedSheetsPerBox,
        feasible,
        resultRsActual,
        targetRsActual,
      });
      if (!feasible) {
        setManualTargetHint(
          manualTargetMode === "suggested"
            ? `Computed RS ${computedRollSize}" does not land in suggested RS group ${manualTargetRs}". Reduce dimensions further until the final effective RS becomes ${manualTargetRs}".`
            : `Computed RS ${computedRollSize}" still does not fit target RS ${plannedBatches.find((b) => b.id === manualTargetBatchId)?.roll_size}".`
        );
      } else {
        setManualTargetHint(null);
      }
    } catch (err) {
      setManualCheckResult(null);
      setManualTargetHint(err.response?.data?.message || "Unable to validate the edited dimensions");
    } finally {
      setManualCheckLoading(false);
    }
  };

  const buildManualReductionHint = (targetValue, isPlanned = false) => {
    if (!manualMoveContext?.job || !targetValue) return null;
    const currentRollSize = getManualCurrentRollSize();
    const currentActual = sootheToActual(currentRollSize);
    const targetRollSize = isPlanned
      ? plannedBatches.find((b) => b.id === targetValue)?.roll_size
      : targetValue;
    const targetActual = sootheToActual(targetRollSize);
    if (!(currentActual > 0) || !(targetActual > 0)) return null;
    if (targetActual >= currentActual) {
      return `Target RS ${targetRollSize}" is higher than or equal to current RS ${currentRollSize}". Use Check to review the computed result, then override if you want to force the job into that higher RS group manually.`;
    }
    return `Current RS ${currentRollSize}" → target RS ${isPlanned ? plannedBatches.find((b) => b.id === targetValue)?.roll_size : targetValue}". Try reducing width and/or height enough to drop roughly ${(currentActual - targetActual).toFixed(3)}" from the effective RS, then click Check.`;
  };

  const handleApplyManualMove = async () => {
    if (!manualMoveContext?.job || (!manualCheckResult?.feasible && !manualOverrideEnabled)) return;
    try {
      setManualApplyLoading(true);
      const forcedSuggestedRollSize = manualOverrideEnabled && manualTargetMode === "suggested" && manualTargetRs
        ? manualTargetRs
        : null;
      const finalRollSize = forcedSuggestedRollSize || manualCheckResult.computedRollSize;
      await updateJob(manualMoveContext.job.id, {
        length: manualDims.length,
        width: manualDims.width,
        height: manualDims.height || 0,
        roll_size: finalRollSize,
        cutting_size: manualCheckResult.computedCuttingSize,
        rs: manualCheckResult.RS,
        cs: manualCheckResult.CS,
        boxes_per_sheet: manualCheckResult.computedBoxesPerSheet,
        sheets_per_box: manualCheckResult.computedSheetsPerBox,
      });

      try {
        await autoSelectMachines(manualMoveContext.job.id);
      } catch (_) { /* non-blocking */ }

      if (manualMoveContext.fromBatchId && manualTargetMode === "planned" && manualTargetBatchId) {
        await moveJobBetweenBatches(manualMoveContext.fromBatchId, manualTargetBatchId, manualMoveContext.job.id);
      } else if (manualMoveContext.fromBatchId && manualTargetMode === "suggested") {
        await removeJobFromBatch(manualMoveContext.fromBatchId, manualMoveContext.job.id);
      } else if (!manualMoveContext.fromBatchId && manualTargetMode === "planned" && manualTargetBatchId) {
        await addJobsToBatch(manualTargetBatchId, [manualMoveContext.job.id]);
      }

      message.success(
        forcedSuggestedRollSize
          ? `Job updated and forced into RS ${forcedSuggestedRollSize}" group`
          : "Job dimensions updated and move applied"
      );
      resetManualMoveState();
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.error || err.response?.data?.message || "Failed to apply manual move");
    } finally {
      setManualApplyLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectJob = (rsKey, jobId) => {
    setSelectedJobs((prev) => {
      const current = prev[rsKey] || [];
      if (current.includes(jobId)) {
        return { ...prev, [rsKey]: current.filter((id) => id !== jobId) };
      }
      return { ...prev, [rsKey]: [...current, jobId] };
    });
  };

  const handleSelectAll = (rsKey, jobIds) => {
    setSelectedJobs((prev) => ({ ...prev, [rsKey]: jobIds }));
  };

  const handleCreateBatch = async () => {
    const jobIds = selectedJobs[activeBatchRS] || [];
    if (jobIds.length < 1) {
      message.warning("Select at least 1 job to create a batch");
      return;
    }
    try {
      await createPlannedBatch(jobIds, batchNotes);
      message.success("Batch created successfully!");
      setCreateModalOpen(false);
      setBatchNotes("");
      setSelectedJobs((prev) => ({ ...prev, [activeBatchRS]: [] }));
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.error || "Failed to create batch");
    }
  };

  const handleGenerateInstructions = async (batchId) => {
    setInstructionsLoading(true);
    try {
      const result = await generateBatchInstructions(batchId);
      setInstructionsData({ batch_id: batchId, instructions: result, saved: false });
      message.success(`Generated ${result.length} stage instructions (preview)`);
    } catch (err) {
      message.error(err.response?.data?.error || "Failed to generate instructions");
    }
    setInstructionsLoading(false);
  };

  const handleSaveInstructions = async (batchId) => {
    setInstructionsLoading(true);
    try {
      const result = await saveBatchInstructions(batchId);
      setInstructionsData({ batch_id: batchId, instructions: result, saved: true });
      message.success(`Saved ${result.length} stage instructions`);
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.error || "Failed to save instructions");
    }
    setInstructionsLoading(false);
  };

  const handleViewSavedInstructions = async (batchId) => {
    setInstructionsLoading(true);
    try {
      const result = await getSavedInstructions(batchId);
      if (result.length > 0) {
        setInstructionsData({ batch_id: batchId, instructions: result, saved: true });
      } else {
        // No saved instructions, generate preview
        handleGenerateInstructions(batchId);
        return;
      }
    } catch (err) {
      message.error(err.response?.data?.error || "Failed to load instructions");
    }
    setInstructionsLoading(false);
  };

  const handleRemoveJobFromBatch = async (batchId, jobId) => {
    try {
      await removeJobFromBatch(batchId, jobId);
      message.success("Job removed from batch — returned to suggestions");
      await fetchData();
      setActiveTab("suggestions");
    } catch (err) {
      message.error(err.response?.data?.error || "Failed to remove job");
    }
  };

  const handleMoveJob = async () => {
    if (!moveJob || !targetBatchId) return;
    try {
      await moveJobBetweenBatches(moveJob.fromBatchId, targetBatchId, moveJob.jobId);
      message.success("Job moved successfully");
      setMoveModalOpen(false);
      setMoveJob(null);
      setTargetBatchId(null);
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.error || "Failed to move job");
    }
  };

  const handleDeleteBatch = async (batchId) => {
    try {
      await deletePlannedBatch(batchId);
      message.success("Batch deleted, jobs returned to suggestions");
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.error || "Failed to delete batch");
    }
  };

  const handleAddJobsToBatch = async (batchId, jobIds) => {
    try {
      await addJobsToBatch(batchId, jobIds);
      message.success("Jobs added to batch");
      setAddToBatchModalOpen(false);
      setAddToBatchTarget(null);
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.error || "Failed to add jobs");
    }
  };

  const handleAddToExistingBatch = async () => {
    if (!addToExistingTargetBatch || addToExistingJobs.length === 0) return;
    try {
      await addJobsToBatch(addToExistingTargetBatch, addToExistingJobs.map((j) => j.id));
      message.success(`${addToExistingJobs.length} job(s) added to existing batch`);
      setAddToExistingModal(false);
      setAddToExistingJobs([]);
      setAddToExistingTargetBatch(null);
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.error || "Failed to add jobs to batch");
    }
  };

  const openAddToExistingModal = (jobs) => {
    setAddToExistingJobs(jobs);
    setAddToExistingTargetBatch(null);
    setAddToExistingModal(true);
  };

  const getAddToExistingCompatibility = (batch) => {
    if (!batch) return { invalid: false, warning: false, message: null };

    const jobRollSizes = addToExistingJobs
      .map((job) => Number(job.roll_size || 0))
      .filter((rollSize) => rollSize > 0);

    if (jobRollSizes.length === 0) {
      return { invalid: false, warning: false, message: null };
    }

    const largestJobRS = Math.max(...jobRollSizes);
    const smallestJobRS = Math.min(...jobRollSizes);

    if (largestJobRS > batch.roll_size) {
      return {
        invalid: true,
        warning: false,
        message: `At least one selected job needs RS ${largestJobRS}" but batch ${batch.batch_number} is only ${batch.roll_size}". This batch cannot fit all selected jobs.`,
      };
    }

    if (smallestJobRS < batch.roll_size) {
      const waste = batch.roll_size - smallestJobRS;
      return {
        invalid: false,
        warning: true,
        message: `Some selected jobs are smaller than batch ${batch.batch_number}. This can waste up to ${waste}" per sheet for the smallest selected job.`,
      };
    }

    return { invalid: false, warning: false, message: null };
  };

  const handleRunBatchMRPAndInstructions = async (batchId) => {
    setMrpLoading(true);
    try {
      const mrp = await getBatchMRP(batchId);
      setMrpData(mrp);
      message.success("Batch MRP computed");
    } catch (err) {
      message.error("Failed to run batch MRP");
    }
    setMrpLoading(false);
  };

  const jobColumns = [
    {
      title: "Job #",
      dataIndex: "job_number",
      width: 160,
      render: (v, job) => (
        <Button
          type="link"
          size="small"
          style={{ padding: 0, fontWeight: 600, whiteSpace: "nowrap" }}
          onClick={(e) => { e.stopPropagation(); setJobModalId(job.id); }}
        >
          {v}
        </Button>
      ),
    },
    {
      title: "Customer",
      dataIndex: ["order", "customer", "name"],
      width: 160,
      ellipsis: true,
    },
    {
      title: "Order #",
      dataIndex: ["order", "order_number"],
      width: 135,
      render: (v) => v || "-",
    },
    {
      title: "PO #",
      dataIndex: ["order", "customer_po_number"],
      width: 110,
      render: (v) => v || "-",
    },
    {
      title: "Item",
      dataIndex: "item_name",
      render: (v) => <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{v}</span>,
    },
    {
      title: "Box Dimensions",
      width: 140,
      render: (_, job) => {
        const unit = job.unit_type && job.unit_type !== "inches" ? ` ${job.unit_type}` : "";
        if (job.length == null || job.width == null || job.height == null) return "-";
        return (
          <Text style={{ fontSize: 12, whiteSpace: "normal" }}>
            {job.length} x {job.width} x {job.height}{unit}
          </Text>
        );
      },
    },
    {
      title: "Ply",
      dataIndex: "ply",
      width: 60,
      render: (v) => (v ?? "-"),
    },
    {
      title: "RS / CS",
      width: 110,
      render: (_, job) => (
        <Space size={4} direction="vertical" style={{ gap: 2 }}>
          <Text code style={{ fontSize: 11 }}>RS {job.roll_size}"</Text>
          {job.cutting_size > 0 && <Text code style={{ fontSize: 11 }}>CS {job.cutting_size}"</Text>}
        </Space>
      ),
    },
    {
      title: <Tooltip title="M = boxes per sheet · N = sheets per box"><span>M / N</span></Tooltip>,
      width: 80,
      render: (_, job) => (
        <Space size={4} direction="vertical" style={{ gap: 2 }}>
          <Text style={{ fontSize: 11 }}>M={job.boxes_per_sheet ?? "–"}</Text>
          <Text style={{ fontSize: 11 }}>N={job.sheets_per_box ?? "–"}</Text>
        </Space>
      ),
    },
    {
      title: "Qty",
      dataIndex: "production_quantity",
      width: 65,
      render: (v) => v?.toLocaleString(),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      width: 80,
      render: (v) => <Tag color={priorityColors[v]}>{v?.toUpperCase()}</Tag>,
    },
    {
      title: "Delivery",
      dataIndex: "expected_delivery",
      width: 95,
      render: (v) => {
        if (!v) return "-";
        const d = new Date(v);
        const daysLeft = Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
        const color = daysLeft < 0 ? "red" : daysLeft <= 3 ? "orange" : "green";
        return <Tag color={color} style={{ fontSize: 11 }}>{d.toLocaleDateString()}</Tag>;
      },
    },
    {
      title: "Stage",
      dataIndex: "current_stage",
      width: 120,
      render: (v) => (
        <Tag color={v === "inventory_fulfilled" ? "green" : "gold"} style={{ fontSize: 10 }}>
          {v?.replace(/_/g, " ")}
        </Tag>
      ),
    },
  ];

  const renderSuggestions = () => {
    const sortedBatches = [...suggestions.batches].sort((a, b) =>
      rsSortOrder === "asc" ? a.roll_size - b.roll_size : b.roll_size - a.roll_size
    );

    return (
    <div>
      {suggestions.batches.length === 0 && !loading && (
        <Alert message="No eligible jobs found for batching" type="info" showIcon />
      )}

      {suggestions.batches.length > 0 && (
        <div style={{ marginBottom: 8, display: "flex", justifyContent: "flex-end" }}>
          <Button
            size="small"
            icon={rsSortOrder === "asc" ? <SortAscendingOutlined /> : <SortDescendingOutlined />}
            onClick={() => setRsSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))}
          >
            RS {rsSortOrder === "asc" ? "Low → High" : "High → Low"}
          </Button>
        </div>
      )}

      <Collapse accordion>
        {sortedBatches.map((group) => {
          const rsKey = String(group.roll_size);
          const selected = selectedJobs[rsKey] || [];

          return (
            <Panel
              key={rsKey}
              header={
                <Space>
                  <ClusterOutlined />
                  <Text strong>RS {group.roll_size}"</Text>
                  <Badge count={group.job_count} style={{ backgroundColor: "#1890ff" }} />
                  <Text type="secondary">
                    | Qty: {group.total_quantity?.toLocaleString()}
                  </Text>
                  {group.priority_score >= 40 && (
                    <Tag color="red">URGENT</Tag>
                  )}
                  {group.earliest_delivery && (
                    <Tag color="orange">
                      Due: {new Date(group.earliest_delivery).toLocaleDateString()}
                    </Tag>
                  )}
                  {selected.length > 0 && (
                    <Tag color="green">{selected.length} selected</Tag>
                  )}
                </Space>
              }
            >
              <Space style={{ marginBottom: 12 }}>
                <Button
                  size="small"
                  onClick={() => handleSelectAll(rsKey, group.jobs.map((j) => j.id))}
                >
                  Select All
                </Button>
                <Button
                  size="small"
                  onClick={() => setSelectedJobs((prev) => ({ ...prev, [rsKey]: [] }))}
                >
                  Clear
                </Button>
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  disabled={selected.length < 1}
                  onClick={() => {
                    setActiveBatchRS(rsKey);
                    setCreateModalOpen(true);
                  }}
                >
                  Create Batch ({selected.length})
                </Button>
                {plannedBatches.filter((b) => b.status === "created").length > 0 && (
                  <Button
                    size="small"
                    icon={<ArrowRightOutlined />}
                    disabled={selected.length < 1}
                    onClick={() => {
                      const selectedJobObjs = group.jobs.filter((j) => selected.includes(j.id));
                      openAddToExistingModal(selectedJobObjs);
                    }}
                  >
                    Add to Existing Batch ({selected.length})
                  </Button>
                )}
              </Space>

              <Table
                size="small"
                dataSource={group.jobs}
                columns={[
                  ...jobColumns,
                  {
                    title: "Action",
                    width: 120,
                    render: (_, job) => (
                      <Space size={2}>
                        {plannedBatches.filter((b) => b.status === "created").length > 0 ? (
                          <Tooltip title="Add to existing batch">
                            <Button
                              size="small"
                              type="link"
                              icon={<ArrowRightOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                openAddToExistingModal([job]);
                              }}
                            />
                          </Tooltip>
                        ) : null}
                        <Tooltip title="Manual move / lower RS check">
                          <Button
                            size="small"
                            type="link"
                            icon={<EditOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              openManualMoveModal(job.id);
                            }}
                          />
                        </Tooltip>
                      </Space>
                    ),
                  },
                ]}
                rowKey="id"
                pagination={false}
                rowSelection={{
                  selectedRowKeys: selected,
                  onChange: (keys) =>
                    setSelectedJobs((prev) => ({ ...prev, [rsKey]: keys })),
                }}
              />
            </Panel>
          );
        })}
      </Collapse>

      {suggestions.suggestions?.length > 0 && (
        <Card
          title={
            <Space>
              <ThunderboltOutlined />
              <span>Suggestions: Jobs that could fit in larger batches</span>
            </Space>
          }
          style={{ marginTop: 16 }}
          size="small"
        >
          <Table
            size="small"
            dataSource={suggestions.suggestions || []}
            rowKey={(r) => `${r.job_id}-${r.suggested_batch_rs}`}
            pagination={false}
            columns={[
              {
                title: "Job #",
                dataIndex: "job_number",
                width: 150,
                render: (v, row) => (
                  <Button type="link" size="small" style={{ padding: 0, fontWeight: 600 }}
                    onClick={() => setJobModalId(row.job_id)}>
                    {v}
                  </Button>
                ),
              },
              {
                title: "Item / Subcategory",
                dataIndex: "item_name",
                render: (v, row) => (
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{v}</div>
                    {row.subcategory && <Text type="secondary" style={{ fontSize: 11 }}>{row.subcategory}</Text>}
                  </div>
                ),
              },
              {
                title: "Current RS",
                dataIndex: "current_rs",
                width: 90,
                render: (v) => <Text code>{v}"</Text>,
              },
              {
                title: "Suggested RS",
                dataIndex: "suggested_batch_rs",
                width: 110,
                render: (v) => <Text code type="success">{v}"</Text>,
              },
              {
                title: <Tooltip title="M = boxes/sheet · N = sheets/box"><span>M (boxes/sheet)</span></Tooltip>,
                width: 130,
                render: (_, row) => (
                  <Space size={4}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Now: {row.current_boxes_per_sheet}</Text>
                    <Text>→</Text>
                    <Text type="success" strong>{row.new_boxes_per_sheet}</Text>
                  </Space>
                ),
              },
              {
                title: <Tooltip title="N = sheets/box — unchanged by RS grouping"><span>N (sheets/box)</span></Tooltip>,
                width: 120,
                render: (_, row) => (
                  <Text type="secondary" style={{ fontSize: 11 }}>{row.current_sheets_per_box ?? "–"}</Text>
                ),
              },
              {
                title: "Waste",
                dataIndex: "waste_inches",
                width: 65,
                render: (v) => <Text type="success">{v}"</Text>,
              },
              {
                title: "Reason",
                dataIndex: "reason",
                ellipsis: true,
              },
              {
                title: "Action",
                width: 190,
                render: (_, row) => (
                  <Space size={4}>
                    {row.suggestion_applied ? (
                      <Button size="small" danger onClick={() => handleRevertSuggestion(row)}>
                        Revert RS
                      </Button>
                    ) : (
                      <Button size="small" type="primary" ghost onClick={() => handleApplySuggestion(row)}>
                        Apply RS
                      </Button>
                    )}
                    <Tooltip title="Hide this suggestion">
                      <Button size="small" onClick={() => handleRejectSuggestion(row)}>
                        Reject
                      </Button>
                    </Tooltip>
                    <Tooltip title="Manually edit dimensions and move this job">
                      <Button size="small" icon={<EditOutlined />} onClick={() => openManualMoveModal(row.job_id)} />
                    </Tooltip>
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      )}

      {/* Box size reduction suggestions */}
      {(suggestions.boxSizeReductions || []).length > 0 && (
        <Card
          title={
            <Space>
              <ThunderboltOutlined style={{ color: "#fa8c16" }} />
              <span>Box Size Reduction Suggestions</span>
              <Text type="secondary" style={{ fontSize: 12 }}>
                — Reduce W or H slightly to drop RS by 2"
              </Text>
            </Space>
          }
          style={{ marginTop: 16 }}
          size="small"
        >
          <Table
            size="small"
            dataSource={suggestions.boxSizeReductions}
            rowKey="job_id"
            pagination={false}
            columns={[
              {
                title: "Job #",
                dataIndex: "job_number",
                width: 150,
                render: (v, row) => (
                  <Button type="link" size="small" style={{ padding: 0, fontWeight: 600 }}
                    onClick={() => setJobModalId(row.job_id)}>
                    {v}
                  </Button>
                ),
              },
              {
                title: "Item / Subcategory",
                dataIndex: "item_name",
                render: (v, row) => (
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{v}</div>
                    {row.subcategory && <Text type="secondary" style={{ fontSize: 11 }}>{row.subcategory}</Text>}
                  </div>
                ),
              },
              {
                title: "Current Dims (W × H)",
                width: 140,
                render: (_, row) => (
                  <Text style={{ fontSize: 12 }}>
                    {row.current_W}" × {row.current_H}" {row.unit_type !== "inches" ? `(${row.unit_type})` : ""}
                  </Text>
                ),
              },
              {
                title: "RS",
                width: 110,
                render: (_, row) => (
                  <Space size={4}>
                    <Text code>{row.current_rs}"</Text>
                    <Text>→</Text>
                    <Text code type="success">{row.target_rs}"</Text>
                  </Space>
                ),
              },
              {
                title: "Overshoot",
                width: 110,
                render: (_, row) => (
                  <Tooltip title={`W+H is ${row.overshoot_inches}" above the lower RS boundary`}>
                    <Text type="warning" style={{ fontSize: 12 }}>
                      {row.overshoot_inches}" ({row.overshoot_soothes} soothes)
                    </Text>
                  </Tooltip>
                ),
              },
              {
                title: "Reduction needed",
                width: 150,
                render: (_, row) => (
                  <Text style={{ fontSize: 12 }}>
                    {row.reduction_inches}" ({row.reduction_soothes} soothes)
                  </Text>
                ),
              },
              {
                title: "Options",
                render: (_, row) => (
                  <Space size={4} wrap direction="vertical">
                    {row.options.map((opt, i) => (
                      <Tag
                        key={i}
                        color={(selectedReductionOptions[row.job_id] ?? 0) === i ? "blue" : "orange"}
                        style={{ fontSize: 11, margin: 2, cursor: "pointer" }}
                        onClick={() =>
                          setSelectedReductionOptions((prev) => ({ ...prev, [row.job_id]: i }))
                        }
                      >
                        {opt.dimension === "Split W+H"
                          ? `W ${opt.from_W}"→${opt.to_W}" · H ${opt.from_H}"→${opt.to_H}" (${opt.reduction_soothes_each}s each)`
                          : `${opt.dimension}: ${opt.from}"→${opt.to}" (${opt.reduction_soothes} soothes)`
                        }
                      </Tag>
                    ))}
                  </Space>
                ),
              },
              {
                title: "Action",
                width: 180,
                render: (_, row) => (
                  <Space size={4}>
                    <Button size="small" type="primary" ghost onClick={() => handleApplyBoxReduction(row)}>
                      Apply
                    </Button>
                    <Button size="small" onClick={() => handleRejectBoxReduction(row)}>
                      Reject
                    </Button>
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
  };

  const renderPlannedBatches = () => (
    <div>
      <Table
        dataSource={plannedBatches}
        rowKey="id"
        loading={loading}
        columns={[
          {
            title: "Batch #",
            dataIndex: "batch_number",
            width: 160,
            render: (v) => <Text strong>{v}</Text>,
          },
          {
            title: "RS",
            dataIndex: "roll_size",
            width: 70,
            render: (v) => <Text code>{v}"</Text>,
          },
          {
            title: "Jobs",
            dataIndex: "jobs",
            width: 60,
            render: (jobs) => <Badge count={jobs?.length} />,
          },
          {
            title: "Total Qty",
            dataIndex: "total_quantity",
            width: 100,
            render: (v) => v?.toLocaleString(),
          },
          {
            title: "Status",
            dataIndex: "status",
            width: 110,
            render: (v) => {
              const colors = { created: "blue", scheduled: "purple", in_progress: "orange", completed: "green", cancelled: "red" };
              return <Tag color={colors[v]}>{v?.replace(/_/g, " ").toUpperCase()}</Tag>;
            },
          },
          {
            title: "Priority",
            dataIndex: "priority_score",
            width: 80,
            render: (v) => (
              <Badge count={v} style={{ backgroundColor: v >= 40 ? "#f5222d" : v >= 30 ? "#fa8c16" : "#1890ff" }} />
            ),
          },
          {
            title: "Stage",
            dataIndex: ["stage", "name"],
            width: 120,
          },
          {
            title: "Actions",
            width: 320,
            render: (_, record) => (
              <Space size="small" wrap>
                <Tooltip title="Run Batch MRP">
                  <Button
                    size="small"
                    icon={<ShoppingCartOutlined />}
                    onClick={() => handleRunBatchMRPAndInstructions(record.id)}
                  >
                    MRP
                  </Button>
                </Tooltip>
                <Tooltip title={record.instructions_saved ? "View Saved Instructions" : "Generate Work Instructions"}>
                  <Button
                    size="small"
                    type="primary"
                    ghost={!record.instructions_saved}
                    icon={record.instructions_saved ? <CheckCircleOutlined /> : <FileTextOutlined />}
                    onClick={() => record.instructions_saved ? handleViewSavedInstructions(record.id) : handleGenerateInstructions(record.id)}
                    style={record.instructions_saved ? { borderColor: "#52c41a", color: "#52c41a" } : {}}
                  >
                    Instructions {record.instructions_saved && "✓"}
                  </Button>
                </Tooltip>
                <Tooltip title="Add jobs from suggestions">
                  <Button
                    size="small"
                    icon={<PlusOutlined />}
                    disabled={record.status !== "created"}
                    onClick={() => {
                      setAddToBatchTarget(record);
                      setAddToBatchModalOpen(true);
                    }}
                  />
                </Tooltip>
                <Popconfirm
                  title="Delete this batch? Jobs will return to suggestions."
                  onConfirm={() => handleDeleteBatch(record.id)}
                  disabled={record.status !== "created"}
                >
                  <Tooltip title="Delete Batch">
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      disabled={record.status !== "created"}
                    />
                  </Tooltip>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
        expandable={{
          expandedRowRender: (record) => (
            <Table
              size="small"
              dataSource={record.jobs}
              rowKey="id"
              pagination={false}
              columns={[
                {
                  title: "Job #",
                  dataIndex: ["job", "job_number"],
                  width: 120,
                  render: (v, batchJob) => (
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0, fontWeight: 600 }}
                      onClick={() => setJobModalId(batchJob.job?.id || batchJob.job_id)}
                    >
                      {v}
                    </Button>
                  ),
                },
                { title: "Customer", dataIndex: ["job", "order", "customer", "name"], width: 150 },
                { title: "Item", dataIndex: ["job", "item_name"], width: 180 },
                { title: "Qty", dataIndex: ["job", "production_quantity"], width: 80 },
                {
                  title: "Priority",
                  dataIndex: ["job", "priority"],
                  width: 90,
                  render: (v) => <Tag color={priorityColors[v]}>{v?.toUpperCase()}</Tag>,
                },
                {
                  title: "Actions",
                  width: 160,
                  render: (_, batchJob) => (
                    <Space size="small">
                      <Tooltip title="Move to another batch">
                        <Button
                          size="small"
                          icon={<SwapOutlined />}
                          disabled={record.status !== "created"}
                          onClick={() => {
                            setMoveJob({
                              jobId: batchJob.job?.id || batchJob.job_id,
                              fromBatchId: record.id,
                              jobNumber: batchJob.job?.job_number,
                            });
                            setMoveModalOpen(true);
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Manual move / lower RS check">
                        <Button
                          size="small"
                          icon={<EditOutlined />}
                          disabled={record.status !== "created"}
                          onClick={() =>
                            openManualMoveModal(batchJob.job?.id || batchJob.job_id, record.id)
                          }
                        />
                      </Tooltip>
                      <Popconfirm
                        title="Remove this job from the batch?"
                        onConfirm={() =>
                          handleRemoveJobFromBatch(record.id, batchJob.job?.id || batchJob.job_id)
                        }
                        disabled={record.status !== "created"}
                      >
                        <Tooltip title="Remove from batch">
                          <Button
                            size="small"
                            danger
                            icon={<MinusCircleOutlined />}
                            disabled={record.status !== "created"}
                          />
                        </Tooltip>
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]}
            />
          ),
        }}
      />
    </div>
  );

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const result = await getInstructionLogs({ limit: 100 });
      setInstructionLogs(result.logs || []);
    } catch (err) {
      // non-fatal
    } finally {
      setLogsLoading(false);
    }
  };

  const renderHistory = () => (
    <div>
      <div style={{ marginBottom: 12, display: "flex", justifyContent: "flex-end" }}>
        <Button size="small" icon={<ReloadOutlined />} onClick={fetchLogs} loading={logsLoading}>
          Refresh
        </Button>
      </div>
      {instructionLogs.length === 0 && !logsLoading && (
        <Alert
          message="No instruction generation events yet. Generate instructions from the Batch Board to see history here."
          type="info"
          showIcon
        />
      )}
      <Table
        size="small"
        loading={logsLoading}
        dataSource={instructionLogs}
        rowKey="id"
        pagination={{ pageSize: 20 }}
        columns={[
          {
            title: "Date & Time",
            dataIndex: "created_at",
            width: 160,
            render: (v) => v ? new Date(v).toLocaleString() : "–",
            sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
            defaultSortOrder: "descend",
          },
          {
            title: "Stage",
            dataIndex: "stage_name",
            width: 140,
            render: (v, r) => <Tag color="blue">{v || r.stage_code}</Tag>,
          },
          {
            title: "Batches Included",
            dataIndex: "batch_numbers",
            render: (nums) => (
              <Space size={4} wrap>
                {(nums || []).map((n) => (
                  <Tag key={n} color="geekblue" style={{ fontSize: 11 }}>{n}</Tag>
                ))}
              </Space>
            ),
          },
          {
            title: "Jobs",
            dataIndex: "total_jobs",
            width: 65,
            render: (v) => <Tag>{v}</Tag>,
          },
          {
            title: "Total Qty",
            dataIndex: "total_quantity",
            width: 100,
            render: (v) => v?.toLocaleString(),
          },
          {
            title: "Batches #",
            dataIndex: "batch_ids",
            width: 80,
            render: (ids) => <Text type="secondary">{ids?.length || 0}</Text>,
          },
        ]}
      />
    </div>
  );

  const tabItems = [
    {
      key: "suggestions",
      label: (
        <span>
          <ClusterOutlined /> Batch Suggestions
          <Badge
            count={suggestions.batches.filter((b) => b.job_count >= 2).length}
            style={{ marginLeft: 8 }}
          />
        </span>
      ),
      children: renderSuggestions(),
    },
    {
      key: "planned",
      label: (
        <span>
          <CheckCircleOutlined /> Planned Batches
          <Badge count={plannedBatches.length} style={{ marginLeft: 8 }} />
        </span>
      ),
      children: renderPlannedBatches(),
    },
    {
      key: "history",
      label: (
        <span>
          <FileTextOutlined /> Instruction History
        </span>
      ),
      children: renderHistory(),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <ClusterOutlined style={{ fontSize: 20 }} />
            <Title level={4} style={{ margin: 0 }}>
              Batch Planning
            </Title>
          </Space>
        }
        extra={
          <Button onClick={fetchData} loading={loading}>
            Refresh
          </Button>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);
            if (key === "history" && instructionLogs.length === 0) fetchLogs();
          }}
          items={tabItems}
        />
      </Card>

      {/* Create Batch Modal */}
      <Modal
        title="Create Production Batch"
        open={createModalOpen}
        onOk={handleCreateBatch}
        onCancel={() => setCreateModalOpen(false)}
        okText="Create Batch"
      >
        <p>
          Creating batch for <Text strong>RS {activeBatchRS}"</Text> with{" "}
          <Text strong>{(selectedJobs[activeBatchRS] || []).length} jobs</Text>
        </p>
        <TextArea
          placeholder="Notes (optional)"
          value={batchNotes}
          onChange={(e) => setBatchNotes(e.target.value)}
          rows={3}
        />
      </Modal>

      {/* Move Job Modal */}
      <Modal
        title={`Move Job ${moveJob?.jobNumber || ""} to Another Batch`}
        open={moveModalOpen}
        onOk={handleMoveJob}
        onCancel={() => { setMoveModalOpen(false); setMoveJob(null); setTargetBatchId(null); }}
        okText="Move Job"
        okButtonProps={{ disabled: !targetBatchId }}
      >
        <p>Select the target batch to move this job into:</p>
        <Select
          style={{ width: "100%" }}
          placeholder="Select target batch"
          value={targetBatchId}
          onChange={setTargetBatchId}
          options={plannedBatches
            .filter((b) => b.id !== moveJob?.fromBatchId && b.status === "created")
            .map((b) => ({
              value: b.id,
              label: `${b.batch_number} — RS ${b.roll_size}" (${b.jobs?.length} jobs, Qty: ${b.total_quantity})`,
            }))}
        />
      </Modal>

      {/* Add Jobs to Batch Modal */}
      <Modal
        title={`Add Jobs to ${addToBatchTarget?.batch_number || "Batch"}`}
        open={addToBatchModalOpen}
        onCancel={() => { setAddToBatchModalOpen(false); setAddToBatchTarget(null); }}
        footer={null}
        width={800}
      >
        {addToBatchTarget && (
          <div>
            <Alert
              message={`Select jobs to add to batch ${addToBatchTarget.batch_number} (RS ${addToBatchTarget.roll_size}")`}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            {suggestions.batches
              .filter((g) => g.roll_size === addToBatchTarget.roll_size)
              .map((group) => {
                const existingJobIds = (addToBatchTarget.jobs || []).map((bj) => bj.job?.id || bj.job_id);
                const availableJobs = group.jobs.filter((j) => !existingJobIds.includes(j.id));
                if (availableJobs.length === 0) return <Alert key={group.roll_size} message="No available jobs with matching RS" type="warning" showIcon />;
                return (
                  <Table
                    key={group.roll_size}
                    size="small"
                    dataSource={availableJobs}
                    rowKey="id"
                    pagination={false}
                    rowSelection={{
                      onChange: (keys) => setSelectedJobs((prev) => ({ ...prev, _addToBatch: keys })),
                    }}
                    columns={[
                      { title: "Job #", dataIndex: "job_number", width: 100 },
                      { title: "Customer", dataIndex: ["order", "customer", "name"], width: 150 },
                      { title: "Item", dataIndex: "item_name", width: 180, ellipsis: true },
                      { title: "Qty", dataIndex: "production_quantity", width: 80 },
                      { title: "Priority", dataIndex: "priority", width: 90, render: (v) => <Tag color={priorityColors[v]}>{v?.toUpperCase()}</Tag> },
                    ]}
                    footer={() => (
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        disabled={!(selectedJobs._addToBatch?.length > 0)}
                        onClick={() => handleAddJobsToBatch(addToBatchTarget.id, selectedJobs._addToBatch)}
                      >
                        Add Selected Jobs ({selectedJobs._addToBatch?.length || 0})
                      </Button>
                    )}
                  />
                );
              })}
          </div>
        )}
      </Modal>

      {/* Instructions Modal */}
      <Modal
        title={<Space>
          Batch Work Instructions
          {instructionsData?.saved ? <Tag color="green">SAVED</Tag> : <Tag color="orange">PREVIEW</Tag>}
        </Space>}
        open={!!instructionsData}
        onCancel={() => setInstructionsData(null)}
        footer={instructionsData ? [
          !instructionsData.saved && (
            <Button key="save" type="primary" icon={<SaveOutlined />}
              loading={instructionsLoading}
              onClick={() => handleSaveInstructions(instructionsData.batch_id)}
            >
              Save Instructions
            </Button>
          ),
          <Button key="regenerate" icon={<ReloadOutlined />}
            loading={instructionsLoading}
            onClick={() => handleGenerateInstructions(instructionsData.batch_id)}
          >
            Regenerate
          </Button>,
          <Button key="close" onClick={() => setInstructionsData(null)}>Close</Button>,
        ].filter(Boolean) : null}
        width={1100}
      >
        {instructionsLoading ? (
          <Spin />
        ) : instructionsData?.instructions?.length > 0 ? (
          <Collapse defaultActiveKey={[instructionsData.instructions[0]?.id]}>
            {instructionsData.instructions.map((inst) => {
              const c = inst.content || {};
              const hdr = c.header || {};
              const rows = c.unified_table || [];
              const totals = c.totals || {};
              const stage = (hdr.stage || "").toLowerCase();

              // Base columns shared by all stages
              const needsLBH = stage === "casting" || stage === "die_cutting" || stage === "joining" || stage === "after_box_print" || stage === "qc";
              const skipRSCS = stage === "after_box_print" || stage === "qc";
              const baseCols = [
                { title: "Job #", dataIndex: "job_number", width: 120, render: (v) => <Text strong>{v}</Text> },
                { title: "Item", dataIndex: "item_name", width: 200, ellipsis: false, render: (v) => <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{v}</span> },
                ...(needsLBH ? [
                  { title: "L", dataIndex: "L", width: 40 },
                  { title: "B", dataIndex: "B", width: 40 },
                  { title: "H", dataIndex: "H", width: 40 },
                ] : []),
                ...(!skipRSCS ? [{ title: "RS", dataIndex: "rs", width: 50, render: (v) => <Text strong>{v}"</Text> }] : []),
                ...(!skipRSCS && stage !== "corrugation" ? [{ title: "CS", dataIndex: "cs", width: 55, render: (v) => v ? <Text strong>{v}"</Text> : "-" }] : []),
              ];

              // Stage-specific extra columns
              let extraCols = [];
              let summaryRow = null;

              if (stage === "corrugation") {
                extraCols = [
                  { title: "Liner (Material)", dataIndex: "liner_material", width: 120 },
                  { title: "Liner (Kg)", dataIndex: "liner_kg", width: 80 },
                  { title: "Flute (Material)", dataIndex: "flute_material", width: 120 },
                  { title: "Flute (Kg)", dataIndex: "flute_kg", width: 80 },
                  { title: "FF", dataIndex: "flute_factor", width: 50 },
                ];
                summaryRow = () => (
                  <Table.Summary.Row style={{ fontWeight: "bold", background: "#fafafa" }}>
                    <Table.Summary.Cell colSpan={3}>TOTAL</Table.Summary.Cell>
                    <Table.Summary.Cell />
                    <Table.Summary.Cell>{totals.liner_kg}</Table.Summary.Cell>
                    <Table.Summary.Cell />
                    <Table.Summary.Cell>{totals.flute_kg}</Table.Summary.Cell>
                    <Table.Summary.Cell />
                  </Table.Summary.Row>
                );
              } else if (stage === "sheet_cutting") {
                extraCols = [
                  { title: "Box Qty", dataIndex: "box_qty", width: 70, render: (v) => v?.toLocaleString() },
                  { title: "Ply", dataIndex: "ply", width: 40 },
                  { title: "Boxes/Sheet", dataIndex: "M", width: 40, render: (v) => v || "-" },
                  { title: "Sheets/Box", dataIndex: "N", width: 40, render: (v) => v || "-" },
                  { title: "2-Ply (Qty)", dataIndex: "two_ply_sheets", width: 80, render: (v) => v?.toLocaleString() },
                  { title: "2-Ply (Material)", dataIndex: "two_ply_material", width: 200, render: (v) => <span style={{ fontSize: 11 }}>{v}</span> },
                  { title: "Top (Qty)", dataIndex: "top_paper_sheets", width: 75, render: (v) => v?.toLocaleString() },
                  { title: "Top (Material)", dataIndex: "top_paper_material", width: 130, render: (v) => <span style={{ fontSize: 11 }}>{v}</span> },
                ];
                summaryRow = () => (
                  <Table.Summary.Row style={{ fontWeight: "bold", background: "#fafafa" }}>
                    <Table.Summary.Cell colSpan={4}>TOTAL</Table.Summary.Cell>
                    <Table.Summary.Cell>{totals.box_qty?.toLocaleString()}</Table.Summary.Cell>
                    <Table.Summary.Cell />
                    <Table.Summary.Cell />
                    <Table.Summary.Cell />
                    <Table.Summary.Cell>{totals.two_ply_sheets?.toLocaleString()}</Table.Summary.Cell>
                    <Table.Summary.Cell />
                    <Table.Summary.Cell>{totals.top_paper_sheets?.toLocaleString()}</Table.Summary.Cell>
                    <Table.Summary.Cell />
                  </Table.Summary.Row>
                );
              } else if (stage === "pasting") {
                extraCols = [
                  { title: "Ply", dataIndex: "ply", width: 40 },
                  { title: "Box Qty", dataIndex: "box_qty", width: 70, render: (v) => v?.toLocaleString() },
                  { title: "Boxes/Sheet", dataIndex: "M", width: 40, render: (v) => v || "-" },
                  { title: "Sheets/Box", dataIndex: "N", width: 40, render: (v) => v || "-" },
                  { title: "Sheets", dataIndex: "sheet_qty", width: 70, render: (v) => v?.toLocaleString() },
                  { title: "Passes", dataIndex: "passes", width: 55 },
                ];
                summaryRow = () => (
                  <Table.Summary.Row style={{ fontWeight: "bold", background: "#fafafa" }}>
                    <Table.Summary.Cell colSpan={4}>TOTAL</Table.Summary.Cell>
                    <Table.Summary.Cell />
                    <Table.Summary.Cell>{totals.box_qty?.toLocaleString()}</Table.Summary.Cell>
                    <Table.Summary.Cell />
                    <Table.Summary.Cell />
                    <Table.Summary.Cell>{totals.sheet_qty?.toLocaleString()}</Table.Summary.Cell>
                    <Table.Summary.Cell />
                  </Table.Summary.Row>
                );
              } else if (stage === "after_box_print") {
                extraCols = [
                  { title: "Ply", dataIndex: "ply", width: 40 },
                  { title: "Box Qty", dataIndex: "box_qty", width: 70, render: (v) => v?.toLocaleString() },
                  { title: "Printing", dataIndex: "print_method", width: 75 },
                  { title: "Sides", dataIndex: "printing_sides", width: 50 },
                  { title: "Colors", dataIndex: "printing_colors", width: 70 },
                  { title: "Spl Colors", dataIndex: "printing_special_colors", width: 75 },
                  { title: "PO Marking", dataIndex: "po_marking_url", width: 90, render: (v) => v ? <a href={v} target="_blank" rel="noreferrer">View PDF</a> : <Text type="danger">Missing</Text> },
                ];
                summaryRow = () => (
                  <Table.Summary.Row style={{ fontWeight: "bold", background: "#fafafa" }}>
                    <Table.Summary.Cell colSpan={5}>TOTAL</Table.Summary.Cell>
                    <Table.Summary.Cell />
                    <Table.Summary.Cell>{totals.box_qty?.toLocaleString()}</Table.Summary.Cell>
                    <Table.Summary.Cell colSpan={5} />
                  </Table.Summary.Row>
                );
              } else if (stage === "pre_print") {
                extraCols = [
                  { title: "Sheets", dataIndex: "sheet_qty", width: 70, render: (v) => v?.toLocaleString() },
                  { title: "Boxes", dataIndex: "box_qty", width: 70, render: (v) => v?.toLocaleString() },
                  { title: "Printing", dataIndex: "print_method", width: 75 },
                  { title: "Colors", dataIndex: "colors", width: 55 },
                  { title: "Waste", dataIndex: "waste", width: 55, render: (v) => v > 0 ? <Text type="warning">{v}"</Text> : <Text type="success">0</Text> },
                ];
              } else if (stage === "casting" || stage === "die_cutting") {
                extraCols = [
                  { title: "Ply", dataIndex: "ply", width: 40 },
                  { title: "Box Qty", dataIndex: "box_qty", width: 70, render: (v) => v?.toLocaleString() },
                  { title: "Boxes/Sheet", dataIndex: "M", width: 40, render: (v) => v || "-" },
                  { title: "Sheets/Box", dataIndex: "N", width: 40, render: (v) => v || "-" },
                  { title: "Sheets", dataIndex: "sheet_qty", width: 70, render: (v) => v?.toLocaleString() },
                ];
                summaryRow = () => (
                  <Table.Summary.Row style={{ fontWeight: "bold", background: "#fafafa" }}>
                    <Table.Summary.Cell colSpan={7}>TOTAL</Table.Summary.Cell>
                    <Table.Summary.Cell />
                    <Table.Summary.Cell>{totals.box_qty?.toLocaleString()}</Table.Summary.Cell>
                    <Table.Summary.Cell />
                    <Table.Summary.Cell />
                    <Table.Summary.Cell>{totals.sheet_qty?.toLocaleString()}</Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              } else if (stage === "joining") {
                extraCols = [
                  { title: "Ply", dataIndex: "ply", width: 40 },
                  { title: "Box Qty", dataIndex: "box_qty", width: 70, render: (v) => v?.toLocaleString() },
                  { title: "Boxes/Sheet", dataIndex: "M", width: 40, render: (v) => v || "-" },
                  { title: "Sheets/Box", dataIndex: "N", width: 40, render: (v) => v || "-" },
                  { title: "Sheets", dataIndex: "sheet_qty", width: 70, render: (v) => v?.toLocaleString() },
                  { title: "Joining Method", dataIndex: "joining_method", width: 100 },
                ];
                summaryRow = () => (
                  <Table.Summary.Row style={{ fontWeight: "bold", background: "#fafafa" }}>
                    <Table.Summary.Cell colSpan={7}>TOTAL</Table.Summary.Cell>
                    <Table.Summary.Cell />
                    <Table.Summary.Cell>{totals.box_qty?.toLocaleString()}</Table.Summary.Cell>
                    <Table.Summary.Cell />
                    <Table.Summary.Cell />
                    <Table.Summary.Cell>{totals.sheet_qty?.toLocaleString()}</Table.Summary.Cell>
                    <Table.Summary.Cell />
                  </Table.Summary.Row>
                );
              } else if (stage === "qc") {
                extraCols = [
                  { title: "Ply", dataIndex: "ply", width: 40 },
                  { title: "Box Qty", dataIndex: "box_qty", width: 70, render: (v) => v?.toLocaleString() },
                  { title: "Joining", dataIndex: "joining_method", width: 90 },
                  { title: "Printing", dataIndex: "print_method", width: 75 },
                  { title: "Sides", dataIndex: "printing_sides", width: 50 },
                  { title: "Colors", dataIndex: "printing_colors", width: 70 },
                  { title: "Spl Colors", dataIndex: "printing_special_colors", width: 75 },
                  { title: "PO Marking", dataIndex: "po_marking_url", width: 90, render: (v) => v ? <a href={v} target="_blank" rel="noreferrer">View PDF</a> : <Text type="danger">Missing</Text> },
                ];
                summaryRow = () => (
                  <Table.Summary.Row style={{ fontWeight: "bold", background: "#fafafa" }}>
                    <Table.Summary.Cell colSpan={5}>TOTAL</Table.Summary.Cell>
                    <Table.Summary.Cell />
                    <Table.Summary.Cell>{totals.box_qty?.toLocaleString()}</Table.Summary.Cell>
                    <Table.Summary.Cell colSpan={6} />
                  </Table.Summary.Row>
                );
              } else {
                extraCols = [
                  { title: "Ply", dataIndex: "ply", width: 40 },
                  { title: "Box Qty", dataIndex: "box_qty", width: 70, render: (v) => v?.toLocaleString() },
                  { title: "Joining", dataIndex: "joining_method", width: 90 },
                  { title: "Printing", dataIndex: "print_method", width: 75 },
                  { title: "Sides", dataIndex: "printing_sides", width: 50 },
                  { title: "Colors", dataIndex: "printing_colors", width: 70 },
                  { title: "Spl Colors", dataIndex: "printing_special_colors", width: 75 },
                  { title: "PO Marking", dataIndex: "po_marking_url", width: 90, render: (v) => v ? <a href={v} target="_blank" rel="noreferrer">View PDF</a> : <Text type="danger">Missing</Text> },
                ];
              }

              return (
                <Panel
                  key={inst.id}
                  header={
                    <Space>
                      <FileTextOutlined />
                      <Text strong>{inst.stage_name}</Text>
                      <Tag>{inst.instruction_type}</Tag>
                      <Tag color={inst.status === "GENERATED" ? "blue" : "green"}>{inst.status}</Tag>
                      <Text type="secondary">
                        {hdr.total_jobs} jobs · {hdr.total_box_quantity?.toLocaleString()} boxes · {hdr.total_sheet_quantity?.toLocaleString()} sheets
                      </Text>
                    </Space>
                  }
                >

                  {/* Single unified table */}
                  <Table
                    size="small"
                    dataSource={rows}
                    rowKey="job_number"
                    pagination={false}
                    bordered
                    columns={[...baseCols, ...extraCols]}
                    summary={summaryRow || undefined}
                    style={{ marginBottom: 12 }}
                  />

                  {/* Materials inline */}
                  {inst.materials?.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      {inst.materials.map((m) => (
                        <div key={m.name} style={{ display: "flex", alignItems: "center", padding: "4px 8px", background: "#fafafa", borderBottom: "1px solid #f0f0f0" }}>
                          <Text style={{ minWidth: 220 }}>{m.name}</Text>
                          <Text strong>{m.quantity} {m.unit}</Text>
                        </div>
                      ))}
                    </div>
                  )}
                </Panel>
              );
            })}
          </Collapse>
        ) : (
          <Alert message="No instructions generated yet" type="info" showIcon />
        )}
      </Modal>

      {/* MRP Modal */}
      <Modal
        title="Batch MRP - Material Requirements"
        open={!!mrpData}
        onCancel={() => setMrpData(null)}
        footer={mrpData ? [
          <Button key="instructions" type="primary" icon={<FileTextOutlined />}
            onClick={() => { setMrpData(null); handleGenerateInstructions(mrpData.batch_id); }}
          >
            Generate Instructions
          </Button>,
          <Button key="close" onClick={() => setMrpData(null)}>Close</Button>,
        ] : null}
        width={700}
      >
        {mrpLoading ? (
          <Spin />
        ) : mrpData ? (
          <div>
            <Descriptions size="small" bordered column={3} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Batch">{mrpData.batch_number}</Descriptions.Item>
              <Descriptions.Item label="Jobs">{mrpData.job_count}</Descriptions.Item>
              <Descriptions.Item label="Total Qty">{mrpData.total_quantity?.toLocaleString()}</Descriptions.Item>
            </Descriptions>

            {mrpData.all_fulfilled ? (
              <Alert message="All materials fulfilled" type="success" showIcon style={{ marginBottom: 16 }} />
            ) : (
              <Alert message="Some materials have shortages" type="warning" showIcon icon={<ExclamationCircleOutlined />} style={{ marginBottom: 16 }} />
            )}

            <Table
              size="small"
              dataSource={mrpData.materials}
              rowKey={(r) => r.material_id || r.material_name}
              pagination={false}
              expandable={{
                expandedRowRender: (record) => record.reels?.length > 0 ? (
                  <div style={{ padding: "4px 0" }}>
                    <Text strong style={{ fontSize: 12 }}>Available Reels:</Text>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>
                      {record.reels.map((r, i) => (
                        <Tag key={i} color="blue">{r.width}" — {r.qty} reel{r.qty > 1 ? "s" : ""} ({r.weight} kg) {r.reel_number ? `[${r.reel_number}]` : ""}</Tag>
                      ))}
                    </div>
                  </div>
                ) : null,
                rowExpandable: (record) => record.reels?.length > 0,
              }}
              columns={[
                { title: "Material", dataIndex: "material_name", width: 160 },
                { title: "Type", dataIndex: "material_type", width: 90 },
                { title: "RS", dataIndex: "reel_width", width: 60, render: (v) => v ? <Text strong>{v}"</Text> : "-" },
                { title: "Stage", dataIndex: "stage", width: 100 },
                { title: "Required", dataIndex: "required_qty", width: 100, render: (v, r) => `${v?.toLocaleString()} ${r.unit || "kg"}` },
                { title: "Available", dataIndex: "available_qty", width: 100, render: (v, r) => `${v?.toLocaleString()} ${r.unit || "kg"}` },
                { title: "Shortage", dataIndex: "shortage_qty", width: 100, render: (v, r) => v > 0 ? <Text type="danger">{v.toLocaleString()} {r.unit || "kg"}</Text> : <Text type="success">0</Text> },
                {
                  title: "Status",
                  dataIndex: "status",
                  width: 90,
                  render: (v) => {
                    const colors = { fulfilled: "green", partial: "orange", critical: "red" };
                    return <Tag color={colors[v]}>{v?.toUpperCase()}</Tag>;
                  },
                },
              ]}
            />
          </div>
        ) : null}
      </Modal>
      {/* Add Suggestion Jobs to Existing Batch Modal */}
      <Modal
        title="Add Jobs to Existing Batch"
        open={addToExistingModal}
        onOk={handleAddToExistingBatch}
        onCancel={() => { setAddToExistingModal(false); setAddToExistingJobs([]); setAddToExistingTargetBatch(null); }}
        okText="Add to Batch"
        okButtonProps={{
          disabled:
            !addToExistingTargetBatch ||
            getAddToExistingCompatibility(
              plannedBatches.find((b) => b.id === addToExistingTargetBatch)
            ).invalid,
        }}
        width={700}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>Jobs to add:</Text>
          <Table
            size="small"
            dataSource={addToExistingJobs}
            rowKey="id"
            pagination={false}
            style={{ marginTop: 8, marginBottom: 16 }}
            columns={[
              { title: "Job #", dataIndex: "job_number", width: 120 },
              { title: "Item", dataIndex: "item_name", width: 200, ellipsis: true },
              { title: "RS", dataIndex: "roll_size", width: 70, render: (v) => <Text code>{v}"</Text> },
              { title: "Qty", dataIndex: "production_quantity", width: 80 },
            ]}
          />
        </div>
        <Divider />
        <Text strong>Select target batch:</Text>
        <Select
          style={{ width: "100%", marginTop: 8 }}
          placeholder="Select a planned batch..."
          value={addToExistingTargetBatch}
          onChange={setAddToExistingTargetBatch}
          optionLabelProp="label"
        >
          {plannedBatches
            .filter((b) => b.status === "created")
            .map((b) => {
              const compatibility = getAddToExistingCompatibility(b);
              return (
                <Select.Option
                  key={b.id}
                  value={b.id}
                  label={b.batch_number}
                  disabled={compatibility.invalid}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>
                      <Text strong>{b.batch_number}</Text>
                      {" — "}
                      <Text code>RS {b.roll_size}"</Text>
                      {" · "}
                      <Text type="secondary">{b.jobs?.length} jobs · Qty: {b.total_quantity}</Text>
                    </span>
                    <span>
                      {compatibility.invalid ? (
                        <Tag color="red">Won't fit</Tag>
                      ) : compatibility.warning ? (
                        <Tag color="orange">Waste likely</Tag>
                      ) : (
                        <Tag color="green">Same RS</Tag>
                      )}
                    </span>
                  </div>
                </Select.Option>
              );
            })}
        </Select>
        {addToExistingTargetBatch && (() => {
          const batch = plannedBatches.find((b) => b.id === addToExistingTargetBatch);
          const compatibility = getAddToExistingCompatibility(batch);
          if (batch && compatibility.message) {
            return (
              <Alert
                style={{ marginTop: 12 }}
                type={compatibility.invalid ? "error" : "warning"}
                showIcon
                message={compatibility.message}
              />
            );
          }
          return null;
        })()}
      </Modal>

      <Modal
        title={`Manual Move${manualMoveContext?.job?.job_number ? ` — ${manualMoveContext.job.job_number}` : ""}`}
        open={manualMoveModalOpen}
        onCancel={resetManualMoveState}
        onOk={handleApplyManualMove}
        okText={
          manualTargetMode === "planned"
            ? (manualMoveContext?.fromBatchId ? "Apply & Move to Batch" : "Apply & Add to Batch")
            : (manualMoveContext?.fromBatchId ? "Apply & Return to Suggestions" : "Apply Specs")
        }
        okButtonProps={{
          disabled: !(manualCheckResult && (manualCheckResult.feasible || manualOverrideEnabled)),
          loading: manualApplyLoading,
          danger: !!(manualCheckResult && !manualCheckResult.feasible && manualOverrideEnabled),
        }}
        width="min(960px, calc(100vw - 24px))"
        bodyStyle={{ maxHeight: "calc(100vh - 180px)", overflowY: "auto", overflowX: "hidden" }}
      >
        {manualMoveContext?.job ? (
          <Space direction="vertical" style={{ width: "100%" }} size={14}>
            <Descriptions size="small" bordered column={3}>
              <Descriptions.Item label="Current RS">{getManualCurrentRollSize() || "-"}</Descriptions.Item>
              <Descriptions.Item label="Current CS">{getManualCurrentCuttingSize() || "-"}</Descriptions.Item>
              <Descriptions.Item label="Source">
                {manualMoveContext.fromBatchId
                  ? plannedBatches.find((b) => b.id === manualMoveContext.fromBatchId)?.batch_number || "Planned Batch"
                  : "Suggestions"}
              </Descriptions.Item>
            </Descriptions>

            <Space wrap>
              <Text strong>Target:</Text>
              <Select
                value={manualTargetMode}
                style={{ width: 180 }}
                  onChange={(value) => {
                    setManualTargetMode(value);
                    setManualTargetBatchId(null);
                    setManualTargetRs(null);
                    setManualCheckResult(null);
                    setManualTargetHint(null);
                    setManualReductionSuggestion(null);
                  }}
                options={[
                  { value: "suggested", label: "Suggested RS Group" },
                  { value: "planned", label: "Planned Batch" },
                ]}
              />
              {manualTargetMode === "suggested" ? (
                <Select
                  value={manualTargetRs}
                  style={{ width: 220 }}
                  placeholder="Select target RS"
                  onChange={(value) => {
                    setManualTargetRs(value);
                    setManualCheckResult(null);
                    setManualOverrideEnabled(false);
                    setManualTargetHint(buildManualReductionHint(value, false));
                    setManualReductionSuggestion(null);
                    loadManualReductionSuggestion(manualMoveContext.job.id, value, false);
                  }}
                  options={[...suggestions.batches]
                    .filter((g) => {
                      const currentRollSize = getManualCurrentRollSize();
                      if (!currentRollSize) return true;
                      return String(g.roll_size) !== String(currentRollSize);
                    })
                    .sort((a, b) => sootheToActual(a.roll_size) - sootheToActual(b.roll_size))
                    .map((g) => ({
                      value: g.roll_size,
                      label: `RS ${g.roll_size}" (${g.job_count} jobs)`,
                    }))}
                />
              ) : (
                <Select
                  value={manualTargetBatchId}
                  style={{ width: 320 }}
                  placeholder="Select planned batch"
                  onChange={(value) => {
                    setManualTargetBatchId(value);
                    setManualCheckResult(null);
                    setManualOverrideEnabled(false);
                    setManualTargetHint(buildManualReductionHint(value, true));
                    setManualReductionSuggestion(null);
                    loadManualReductionSuggestion(manualMoveContext.job.id, value, true);
                  }}
                  options={plannedBatches
                    .filter((b) => b.status === "created" && b.id !== manualMoveContext.fromBatchId)
                    .map((b) => ({
                      value: b.id,
                      label: `${b.batch_number} — RS ${b.roll_size}"`,
                    }))}
                />
              )}
            </Space>

            <Alert
              type="info"
              showIcon
              message="Edit the dimensions, run Check, and then either apply a feasible move or use override to force the job into the selected RS group."
            />

            {manualSuggestionLoading ? (
              <Spin size="small" />
            ) : manualReductionSuggestion ? (
              <Alert
                type="warning"
                showIcon
                message={`Reduction suggestion: current RS ${manualReductionSuggestion.current_rs}" can drop to ${manualReductionSuggestion.target_rs}" by reducing width and/or height.`}
                description={(
                  <Space direction="vertical" style={{ width: "100%" }} size={8}>
                    <Text type="secondary">
                      Required reduction: {manualReductionSuggestion.reduction_inches}" ({manualReductionSuggestion.reduction_soothes} soothes)
                    </Text>
                    <Space wrap>
                      {manualReductionSuggestion.options.map((opt, index) => (
                        <Button
                          key={`${opt.dimension}-${index}`}
                          size="small"
                          onClick={() => {
                            if (opt.dimension === "Width (W)") {
                              setManualDims((prev) => ({ ...prev, width: opt.to }));
                            } else if (opt.dimension === "Height (H)") {
                              setManualDims((prev) => ({ ...prev, height: opt.to }));
                            } else {
                              setManualDims((prev) => ({
                                ...prev,
                                width: opt.to_W,
                                height: opt.to_H,
                              }));
                            }
                            setManualCheckResult(null);
                            setManualOverrideEnabled(false);
                          }}
                        >
                          {opt.dimension === "Split W+H"
                            ? `Use W ${opt.from_W}"->${opt.to_W}" and H ${opt.from_H}"->${opt.to_H}"`
                            : `Use ${opt.dimension}: ${opt.from}"->${opt.to}"`}
                        </Button>
                      ))}
                    </Space>
                  </Space>
                )}
              />
            ) : null}

            <Descriptions size="small" bordered column={1}>
              <Descriptions.Item label="Length">
                <InputNumber
                  style={{ width: "100%" }}
                  value={manualDims.length}
                  onChange={(value) => {
                    setManualDims((prev) => ({ ...prev, length: value }));
                    setManualCheckResult(null);
                    setManualOverrideEnabled(false);
                  }}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Width">
                <InputNumber
                  style={{ width: "100%" }}
                  value={manualDims.width}
                  onChange={(value) => {
                    setManualDims((prev) => ({ ...prev, width: value }));
                    setManualCheckResult(null);
                    setManualOverrideEnabled(false);
                  }}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Height">
                <InputNumber
                  style={{ width: "100%" }}
                  value={manualDims.height}
                  onChange={(value) => {
                    setManualDims((prev) => ({ ...prev, height: value }));
                    setManualCheckResult(null);
                    setManualOverrideEnabled(false);
                  }}
                />
              </Descriptions.Item>
            </Descriptions>

            <Space>
              <Button type="primary" ghost loading={manualCheckLoading} onClick={handleCheckManualMove}>
                Check
              </Button>
              {manualTargetHint && <Text type="warning">{manualTargetHint}</Text>}
            </Space>

            {manualCheckResult && (
              <Space direction="vertical" style={{ width: "100%" }} size={10}>
                <Descriptions size="small" bordered column={4}>
                  <Descriptions.Item label="Computed RS">{manualCheckResult.computedRollSize || manualCheckResult.RS || manualCheckResult.roll_size || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Computed CS">{manualCheckResult.computedCuttingSize || manualCheckResult.CS || manualCheckResult.cutting_size || "-"}</Descriptions.Item>
                  <Descriptions.Item label="M">{manualCheckResult.computedBoxesPerSheet ?? manualCheckResult.boxes_per_sheet ?? manualCheckResult.M ?? "-"}</Descriptions.Item>
                  <Descriptions.Item label="N">{manualCheckResult.computedSheetsPerBox ?? manualCheckResult.sheets_per_box ?? manualCheckResult.N ?? "-"}</Descriptions.Item>
                  <Descriptions.Item label="Result" span={4}>
                    {manualCheckResult.feasible ? (
                      <Tag color="green">Feasible for target RS</Tag>
                    ) : (
                      <Tag color="red">Not feasible for target RS</Tag>
                    )}
                  </Descriptions.Item>
                </Descriptions>

                {!manualCheckResult.feasible && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Override available"
                    description={(
                      <Checkbox
                        checked={manualOverrideEnabled}
                        onChange={(e) => setManualOverrideEnabled(e.target.checked)}
                      >
                        Force apply this change anyway, even though it does not fit the selected target.
                      </Checkbox>
                    )}
                  />
                )}
              </Space>
            )}
          </Space>
        ) : (
          <Spin />
        )}
      </Modal>

      {/* Job Detail Modal */}
      <Modal
        open={!!jobModalId}
        onCancel={() => setJobModalId(null)}
        footer={null}
        width={1100}
        style={{ top: 20 }}
        destroyOnClose
      >
        {jobModalId && (
          <JobDetail jobId={jobModalId} isModal onClose={() => setJobModalId(null)} />
        )}
      </Modal>
    </div>
  );
}

export default BatchPlanning;
